import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import i18n from '../../i18n/config';
import { TIMEOUTS } from '../config/constants';
import { QK } from '../model/queryKeys';
import { humanizeDetailText } from '../notifications';
import { useNotificationStore } from '../store/notificationStore';
import { api as httpApi } from './client';
import {
  getAlgoSysAPI,
  type SimpleAgentCreateRequest as OrvalSimpleAgentCreateRequest,
} from './generated';

// Канон: один способ чтения на домен — agents (list/get), definition, strategy-config, signals, trades, stats.
// Hooks → Orval-generated (SSOT) или явные api.get на стабильные эндпоинты.
const api = getAlgoSysAPI();

// UI может передавать дополнительные опциональные поля, даже если OpenAPI их не описывает явно.
// Backend игнорирует незнакомые поля, а для известных (historyLookbackHours) — использует.
export type SimpleAgentCreateRequest = OrvalSimpleAgentCreateRequest & {
  autostart?: boolean;
  history_lookback_hours?: number;
  historyLookbackHours?: number;
  strategy_spec: { spec_id: string; version: string; digest?: string };
};

// При ошибке (нет соединения с API) реже опрашивать, чтобы не тормозить UI
const REFETCH_INTERVAL_MS = 20000;
const REFETCH_INTERVAL_WHEN_ERROR_MS = 30000;

export const agentQueryKeys = {
  list: () => QK.agentList(),
  get: (agentId: string) => QK.agentDetail(agentId),
  definition: (agentId: string) => QK.agentDefinition(agentId),
  strategyConfig: (agentId: string) => QK.agentStrategyConfig(agentId),
};

export function useListAgents() {
  return useQuery({
    queryKey: agentQueryKeys.list(),
    queryFn: async () => api.listAgentsApiAgentsGet(),
    staleTime: 5000,
    refetchInterval: (query) => (query.state.status === 'error' ? REFETCH_INTERVAL_WHEN_ERROR_MS : REFETCH_INTERVAL_MS),
    refetchOnWindowFocus: true,
  });
}

export function useGetAgent(agentId: string) {
  return useQuery({
    queryKey: agentQueryKeys.get(agentId),
    queryFn: async () => api.getAgentApiAgentsAgentIdGet(agentId),
    enabled: !!agentId,
    staleTime: 5000,
    refetchInterval: (query) => (query.state.status === 'error' ? REFETCH_INTERVAL_WHEN_ERROR_MS : REFETCH_INTERVAL_MS),
    refetchOnWindowFocus: true,
  });
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SimpleAgentCreateRequest) =>
      api.createAgentApiAgentsPost(payload as OrvalSimpleAgentCreateRequest, {
        timeout: TIMEOUTS.AGENT_START,
        skipErrorNotification: true,
      }),
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: agentQueryKeys.list() });
      const payload = data as { backfill_queued?: boolean };
      if (payload?.backfill_queued) {
        useNotificationStore.getState().push({
          severity: 'info',
          source: 'backfill',
          title: i18n.t('notifications:source.backfill'),
          message: i18n.t('notifications:toasts.agentBackfillQueued'),
          ts: Date.now(),
          dedupKey: 'backfill:agent:create:queued',
          link: '/agents',
        });
      }
    },
  });
}

export interface StrategyPreflightRequest {
  strategy_type?: string;
  strategy_spec: { spec_id: string; version: string; digest?: string };
  params?: Record<string, unknown>;
  indicators?: Record<string, unknown>;
}

export interface StrategyPreflightResponse {
  strategy_type: string;
  effective: {
    params: Record<string, unknown>;
    indicators: Record<string, unknown>;
  };
  context_check?: {
    required_fields?: string[];
    missing_fields?: string[];
    can_start?: boolean;
  };
  run_contract_check?: {
    required_spec_ref?: {
      spec_id?: string;
      version?: string;
      digest?: string;
    } | null;
    can_start?: boolean;
  };
  sources: {
    defaults_for: string;
    runtime_defaults?: {
      params?: Record<string, unknown>;
      indicators?: Record<string, unknown>;
    };
    resolved_spec?: {
      params_defaults?: Record<string, unknown>;
      default_indicators?: Record<string, unknown>;
      digest?: string;
    };
    redis_overrides?: {
      params?: Record<string, unknown>;
      indicators?: Record<string, unknown>;
    };
    request?: {
      params?: Record<string, unknown>;
      indicators?: Record<string, unknown>;
    };
    redis_overrides_applied: boolean;
  };
}

export function useStrategyPreflight() {
  return useMutation({
    mutationFn: async (payload: StrategyPreflightRequest) => {
      const response = await httpApi.post('/api/agents/strategy-preflight', payload, {
        timeout: TIMEOUTS.AGENT_START,
        skipErrorNotification: true,
      });
      return response.data as StrategyPreflightResponse;
    },
  });
}

export async function preflightAgentStart(agentId: string): Promise<StrategyPreflightResponse> {
  const [strategyConfig, agentDefinition] = await Promise.all([
    api.getAgentStrategyConfigApiAgentsAgentIdStrategyConfigGet(agentId),
    api.getAgentDefinitionApiAgentsAgentIdDefinitionGet(agentId),
  ]);
  const cfg = (strategyConfig ?? {}) as unknown as Record<string, unknown>;
  const def = (agentDefinition ?? {}) as unknown as Record<string, unknown>;
  const strategyType =
    (cfg.strategy_type as string | undefined) ||
    (cfg.strategyType as string | undefined) ||
    (((cfg.strategy as Record<string, unknown> | undefined)?.name as string | undefined) ?? 'rsi');
  const params = (cfg.params as Record<string, unknown> | undefined) ?? {};
  const indicators = (cfg.indicators as Record<string, unknown> | undefined) ?? {};
  const strategySpecRaw = (def.strategy_spec as Record<string, unknown> | undefined) ?? undefined;
  const strategySpec =
    strategySpecRaw &&
    typeof strategySpecRaw.spec_id === 'string' &&
    typeof strategySpecRaw.version === 'string'
      ? {
          spec_id: strategySpecRaw.spec_id,
          version: strategySpecRaw.version,
          ...(typeof strategySpecRaw.digest === 'string' ? { digest: strategySpecRaw.digest } : {}),
        }
      : undefined;

  if (!strategySpec) {
    throw new Error(i18n.t('notifications:toasts.noStrategySpec'));
  }
  const response = await httpApi.post('/api/agents/strategy-preflight', {
    strategy_type: strategyType,
    strategy_spec: strategySpec,
    params,
    indicators,
  }, { timeout: TIMEOUTS.AGENT_START });
  return response.data as StrategyPreflightResponse;
}

export function useStartAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) =>
      api.startAgentApiAgentsAgentIdStartPost(agentId, {
        timeout: TIMEOUTS.AGENT_START,
        skipErrorNotification: true,
      }),
    onSuccess: async (data, agentId) => {
      await qc.invalidateQueries({ queryKey: agentQueryKeys.list() });
      await qc.invalidateQueries({ queryKey: agentQueryKeys.definition(agentId) });
      await qc.invalidateQueries({ queryKey: agentQueryKeys.get(agentId) });
      const payload = data as { backfill_queued?: boolean; success?: boolean };
      if (payload?.backfill_queued) {
        useNotificationStore.getState().push({
          severity: 'info',
          source: 'backfill',
          title: i18n.t('notifications:source.backfill'),
          message: i18n.t('notifications:toasts.agentBackfillQueued'),
          ts: Date.now(),
          dedupKey: `backfill:agent:start:queued:${agentId}`,
          link: '/agents',
        });
      }
      if (payload?.success !== false) {
        toast.success(i18n.t('notifications:toasts.agentStarted'));
      } else {
        toast.warning(i18n.t('notifications:toasts.agentStartWarning'));
      }
    },
    onError: (err: unknown) => {
      const ax = err as { response?: { status?: number; data?: { detail?: unknown } } };
      const status = ax?.response?.status;
      const detail = ax?.response?.data?.detail;
      const msgStr = typeof detail === 'string' ? detail : String(detail ?? err);
      const humanMessage = humanizeDetailText(msgStr, i18n.t('notifications:toasts.agentStartErrorFallback'));
      if (status === 503 || /backfill|Session START/i.test(msgStr)) {
        useNotificationStore.getState().push({
          severity: 'warning',
          source: 'backfill',
          title: i18n.t('notifications:toasts.agentStartErrorTitle'),
          message: humanMessage,
          ts: Date.now(),
          dedupKey: 'backfill:agent:start:error',
          link: '/agents',
        });
      }
      toast.error(humanMessage);
    },
  });
}

export function useStopAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) => api.stopAgentApiAgentsAgentIdStopPost(agentId),
    onSuccess: async (_data, agentId) => {
      await qc.invalidateQueries({ queryKey: agentQueryKeys.list() });
      await qc.invalidateQueries({ queryKey: agentQueryKeys.definition(agentId) });
      await qc.invalidateQueries({ queryKey: agentQueryKeys.get(agentId) });
      toast.success(i18n.t('notifications:toasts.agentStopped'));
    },
    onError: (err: unknown) => {
      const msgStr = err instanceof Error ? err.message : String(err);
      toast.error(humanizeDetailText(msgStr, i18n.t('notifications:toasts.agentStopErrorFallback')));
    },
  });
}

export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) => api.deleteAgentApiAgentsAgentIdDelete(agentId),
    onSuccess: async (_data, agentId) => {
      await qc.invalidateQueries({ queryKey: agentQueryKeys.list() });
      await qc.invalidateQueries({ queryKey: agentQueryKeys.definition(agentId) });
      await qc.invalidateQueries({ queryKey: agentQueryKeys.get(agentId) });
      toast.success(i18n.t('notifications:toasts.agentDeleted'));
    },
    onError: (err: unknown) => {
      const msgStr = err instanceof Error ? err.message : String(err);
      toast.error(humanizeDetailText(msgStr, i18n.t('notifications:toasts.agentDeleteErrorFallback')));
    },
  });
}

export interface AgentDefinition {
  id?: string;
  name?: string;
  autostart?: boolean;
  symbol?: string;
  interval?: string;
  strategyName?: string;
  brokerId?: string;
  [key: string]: unknown;
}

export interface AgentDefinitionPatch {
  name?: string;
  autostart?: boolean;
  /** Режим счёта: 'paper' | 'live'. Смена выполняет управляемый рестарт агента на бэкенде. */
  broker_id?: string;
}

export function useGetAgentDefinition(agentId: string) {
  return useQuery({
    queryKey: agentQueryKeys.definition(agentId),
    queryFn: async () => (await api.getAgentDefinitionApiAgentsAgentIdDefinitionGet(agentId)) as AgentDefinition,
    enabled: !!agentId,
    staleTime: 30000,
    refetchInterval: (query) => (query.state.status === 'error' ? REFETCH_INTERVAL_WHEN_ERROR_MS : 60000),
    refetchOnWindowFocus: true,
  });
}

export function usePatchAgentDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { agentId: string; patch: AgentDefinitionPatch }) =>
      (await api.patchAgentDefinitionApiAgentsAgentIdDefinitionPatch(args.agentId, args.patch)) as AgentDefinition,
    onSuccess: async (_data, args) => {
      await qc.invalidateQueries({ queryKey: agentQueryKeys.definition(args.agentId) });
      await qc.invalidateQueries({ queryKey: agentQueryKeys.list() });
      await qc.invalidateQueries({ queryKey: agentQueryKeys.get(args.agentId) });
    },
  });
}

import type { StrategyConfigRequest as GeneratedStrategyConfigRequest } from './generated';

export interface StrategyConfigPatchRequest {
  strategy?: {
    name?: string;
    params?: Record<string, unknown>;
  };
  indicators?: Record<string, Record<string, unknown>>;
  [key: string]: unknown;
}

export type StrategyConfigRequest = GeneratedStrategyConfigRequest;

export function useGetAgentStrategyConfig(agentId: string) {
  return useQuery({
    queryKey: agentQueryKeys.strategyConfig(agentId),
    queryFn: async () => await api.getAgentStrategyConfigApiAgentsAgentIdStrategyConfigGet(agentId),
    enabled: !!agentId,
  });
}

export function usePatchAgentStrategyConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { agentId: string; patch: StrategyConfigPatchRequest }) =>
      await api.patchAgentStrategyConfigApiAgentsAgentIdStrategyConfigPatch(args.agentId, args.patch),
    onSuccess: async (_data, args) => {
      await qc.invalidateQueries({ queryKey: agentQueryKeys.strategyConfig(args.agentId) });
      await qc.invalidateQueries({ queryKey: agentQueryKeys.get(args.agentId) });
      await qc.invalidateQueries({ queryKey: agentQueryKeys.list() });
    },
  });
}


